import React, {useState, useEffect, useRef} from 'react';
import {Input, Button, Table, Row, Space, message, Popconfirm, Typography} from 'antd';
import Highlighter from 'react-highlight-words';
import {SearchOutlined} from '@ant-design/icons';
import {getApiWithAuthToken, deleteApiWithAuthToken} from '../../../api';
import RouteLastCodeForm from './RouteLastCodeForm';

const RouteLastCode = () => {
  const [routeLastCodeList, setRouteLastCodeList] = useState([]);
  const [loading, setLoading] = useState(true);

  const getRouteLastCodeList = () => {
    getApiWithAuthToken('/api/v1/admin/routeLastCodes').then(res => {
      if (!res.error && res.data.length) {
        setRouteLastCodeList(
          res.data.map(each => ({
            id: each.id,
            key: each.id,
            route_id: each.route_id,
            route: each.route_name,
            sales: each.sales,
            collection: each.collection,
            advance_collection: each.advance_collection,
            load_code: each.load_code,
            unload: each.unload,
            settlements: each.settlements,
          }))
        );
      } else {
        if (res.errors) {
          Object.values(res.errors).map(each => message.error(each.msg));
        } else {
          message.error({content: 'Network Error!'});
        }
        setRouteLastCodeList([]);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    getRouteLastCodeList();
  }, []);

  // Delete function
  function handleDeleteClick(id) {
    message.loading({content: 'Processing...', key: id});
    deleteApiWithAuthToken(`/api/v1/admin/routeLastCodes/${id}`).then(res => {
      message.loading({content: 'Loading...', duration: 0.5});
      if (res.error) {
        setTimeout(() => {
          if (res.errors.errno === 1451) {
            message.error({
              content: 'Could not delete!, The routeLastCode is being referred in another place',
              key: id,
            });
          } else {
            message.error({
              content: res.errors.sqlMessage,
              key: id,
            });
          }
        }, 1000);
      } else {
        setTimeout(() => {
          message.success({
            content: 'Deleted!',
            key: id,
          });
        }, 1000);
        getRouteLastCodeList();
      }
    });
  }

  // Edit function

  const [selectedRouteLastCode, setSelectedRouteLastCode] = useState({});
  const handleEditClick = record => {
    setSelectedRouteLastCode(record);
    setIsDrawerVisible(true);
  };

  //Drawer Open function
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const showDrawer = () => {
    setSelectedRouteLastCode({});
    setIsDrawerVisible(true);
  };
  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
  };

  // Table functions

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  var searchInput = useRef(null);

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => (
      <div style={{padding: 8}}>
        <Input
          ref={node => {
            searchInput = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{width: 90}}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{width: 90}}>
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({closeDropdown: false});
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.select(), 100);
      }
    },
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };

  const columns = [
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      ...getColumnSearchProps('route'),
      sorter: (a, b) => (a.route ?? '').localeCompare(b.route ?? ''),
    },
    {
      title: 'Sales',
      dataIndex: 'sales',
      key: 'sales',
    },
    {
      title: 'Collection',
      dataIndex: 'collection',
      key: 'collection',
    },
    {
      title: 'Advance collection',
      dataIndex: 'advance_collection',
      key: 'advance_collection',
    },
    {
      title: 'Load',
      dataIndex: 'load_code',
      key: 'load_code',
    },
    {
      title: 'Unload',
      dataIndex: 'unload',
      key: 'unload',
    },
    {
      title: 'Settlements',
      dataIndex: 'settlements',
      key: 'settlements',
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <>
          <Button size="small" onClick={() => handleEditClick(record)}>
            Edit
          </Button>{' '}
          <span> </span>
          <Popconfirm
            title="Are you sure to delete this routeLastCode?"
            onConfirm={() => handleDeleteClick(record.id)}
          >
            <Button danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" style={{margin: '24px 0'}}>
        <Typography.Title level={3} style={{marginBottom: '0'}}>
          Route Last Code
        </Typography.Title>
        <Button type="primary" onClick={showDrawer}>
          Add
        </Button>
      </Row>
      <Table scroll={{x: 0}} dataSource={routeLastCodeList} columns={columns} loading={loading} />
      <RouteLastCodeForm
        handleDrawerClose={handleDrawerClose}
        isDrawerVisible={isDrawerVisible}
        getRouteLastCodeList={getRouteLastCodeList}
        routeLastCode={selectedRouteLastCode}
      />
    </>
  );
};
export default RouteLastCode;
